import { localStorageToken } from "@/tokens";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { CommonModule } from "@modules/common_module/CommonModule";
import type LocationControllerInterface from "@modules/common_module/LocationController/LocationControllerInterface";
import type { Observable } from "rxjs";
import { catchError, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import {
    REFERRAL_CONTEXT_SEARCH_PARAM,
    REFERRAL_CONTEXT_STORAGE_KEY,
} from "../../../constants/constants";
import RewardsReferralContextApiService from "../api_services/RewardsReferralContextApiService";
import type { RewardsReferralContextQueryParams } from "../types";

@singleton()
class RewardsReferralContextService {
    private readonly locationController: LocationControllerInterface;

    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        @inject(localStorageToken)
        private readonly storage: Storage,
        private readonly commonModule: CommonModule,
        private readonly referralContextApiService: RewardsReferralContextApiService
    ) {
        this.locationController = this.commonModule.exports.locationController;
    }

    private getReferralContextFromStorage(): string | null {
        return this.storage.getItem(REFERRAL_CONTEXT_STORAGE_KEY);
    }

    private getReferralContextFromSearchParams(): string | null {
        const searchParams = this.locationController.getSearchParams();

        return searchParams.get(REFERRAL_CONTEXT_SEARCH_PARAM);
    }

    public getReferralContext(): string | null {
        return (
            this.getReferralContextFromStorage() ||
            this.getReferralContextFromSearchParams()
        );
    }

    public removeReferralContextFromStorage(): void {
        this.storage.removeItem(REFERRAL_CONTEXT_STORAGE_KEY);
    }

    public setReferralContext(
        params: RewardsReferralContextQueryParams
    ): Observable<unknown> {
        return this.referralContextApiService.setReferralContext(params).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsReferralContextApiService.setReferralContext is errored",
                    { params, error }
                );

                throw error;
            }),
            tap(() =>
                this.logger.debug(
                    "RewardsReferralContextApiService.setReferralContext is ready",
                    params
                )
            )
        );
    }
}

export default RewardsReferralContextService;
